import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface DeliverySettings {
  freeDeliveryEnabled: boolean;
  freeDeliveryThreshold: number; // in cents
  deliveryFee: number; // in cents
  promoBannerText: string;
}

const DEFAULTS: DeliverySettings = {
  freeDeliveryEnabled: true,
  freeDeliveryThreshold: 10000,
  deliveryFee: 2500,
  promoBannerText: 'משלוח חינם בהזמנה מעל ₪{threshold}!',
};

async function fetchDeliverySettings(): Promise<DeliverySettings> {
  const { data, error } = await supabase
    .from('site_settings')
    .select('key, value')
    .in('key', ['free_delivery_enabled', 'free_delivery_threshold', 'delivery_fee', 'promo_banner_text']);

  if (error || !data) return DEFAULTS;

  const map = new Map(data.map((r: any) => [r.key, r.value]));

  return {
    freeDeliveryEnabled: map.get('free_delivery_enabled') === 'true',
    freeDeliveryThreshold: parseInt(map.get('free_delivery_threshold') || '10000', 10),
    deliveryFee: parseInt(map.get('delivery_fee') || '2500', 10),
    promoBannerText: map.get('promo_banner_text') || DEFAULTS.promoBannerText,
  };
}

export function useDeliverySettings() {
  return useQuery({
    queryKey: ['delivery-settings'],
    queryFn: fetchDeliverySettings,
    staleTime: 60_000,
  });
}

export function useUpdateDeliverySettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (settings: DeliverySettings) => {
      const updates = [
        { key: 'free_delivery_enabled', value: String(settings.freeDeliveryEnabled) },
        { key: 'free_delivery_threshold', value: String(settings.freeDeliveryThreshold) },
        { key: 'delivery_fee', value: String(settings.deliveryFee) },
        { key: 'promo_banner_text', value: settings.promoBannerText },
      ];

      for (const u of updates) {
        const { error } = await supabase
          .from('site_settings')
          .update({ value: u.value, updated_at: new Date().toISOString() })
          .eq('key', u.key);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['delivery-settings'] });
    },
  });
}
